import type { CSSProperties } from 'react';
import { text } from '../../app/i18n';
import { Icon } from '../../design-system/icons';
import type { Entry } from '../form-targets';
import { optionImageUrl, useFormMedia } from './FormMedia';
import { choiceOptions, optionSourceKey, optionVisual, presentationOf, type ChoiceOption } from './presentation';

/**
 * Choix en tuiles (`radio_tiles`) et choix segmenté (`segmented`) de 1.10.0 : visuel par option (image de la
 * médiathèque, image de l'objet, icône, pastille de couleur), tuiles par rangée, hauteur, visuel au-dessus
 * ou devant, style de la tuile choisie. Un groupe radio : un clic par réponse ; retoucher la tuile choisie
 * d'un choix facultatif l'efface. Présentation seulement : le serveur relit la réponse.
 */

export function OptionVisualMark({ entry, option, size }: { entry: Entry; option: ChoiceOption; size: number }) {
  const media = useFormMedia();
  const visual = optionVisual(entry);
  if (visual === 'dot') return <span className={`ay-fv2-dot ay-fv2-dot--${option.color ?? 'neutral'}`} aria-hidden="true" />;
  if (visual === 'icon') return option.icon === undefined ? null : <Icon name={option.icon} size={Math.round(size * 0.7)} />;
  if (visual === 'image' || visual === 'object_image') {
    const url = optionImageUrl(media, option.image, visual === 'object_image' ? optionSourceKey(entry) : undefined, option.value);
    return url === undefined ? (
      <span className="ay-fv2-tile__noimage" style={{ width: size, height: size }} aria-hidden="true">
        <Icon name="image" size={Math.round(size * 0.55)} />
      </span>
    ) : (
      <img className="ay-fv2-tile__image" src={url} alt="" style={{ width: size, height: size }} />
    );
  }
  return null;
}

export function OptionTiles({
  id,
  entry,
  value,
  onChange,
  disabled,
  required,
  dangerOf,
  attention = false,
  labelledBy,
}: {
  id: string;
  entry: Entry;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled: boolean;
  required: boolean;
  dangerOf?: ((option: string) => boolean) | undefined;
  attention?: boolean;
  labelledBy?: string;
}) {
  const presentation = presentationOf(entry);
  const tiles = presentation.tiles ?? {};
  const options = choiceOptions(entry);
  const current = value === null || value === undefined ? '' : String(value);
  const perRow = tiles.per_row === undefined || tiles.per_row === 'auto' ? undefined : tiles.per_row;
  const position = tiles.visual_position ?? (optionVisual(entry) === 'image' || optionVisual(entry) === 'object_image' ? 'above' : 'start');
  const height = tiles.height ?? 'medium';
  const visualSize = height === 'large' ? 52 : height === 'small' ? 14 : 40;
  const boolean = entry.field.type === 'boolean';
  // Valeur manquante montrée par son texte (`empty_display: placeholder`, « En attente ») : une tuile neutre en tête,
  // choisie tant que rien n'est répondu, qui ramène à l'attente (v1 `status.pending` de la revue vétérinaire).
  const pendingLabel = presentation.empty_display === 'placeholder' ? text((presentation as { placeholder?: Readonly<Record<string, string>> }).placeholder, 'fr', '') : '';
  const pending = pendingLabel !== '';
  // `per_row` compte toutes les tuiles de la rangée, celle de l'attente comprise.
  const grid = (perRow === undefined ? {} : { gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))` }) as CSSProperties;
  return (
    <div className={`ay-fv2-tiles ay-fv2-tiles--${height} ay-fv2-tiles--visual-${position}${attention ? ' ay-fv2-tiles--attention' : ''}`} role="radiogroup" aria-labelledby={labelledBy} style={grid} data-testid={`tiles-${entry.field.field_id}`}>
      {pending && (
        <button
          id={`${id}-pending`}
          type="button"
          role="radio"
          aria-checked={current === ''}
          disabled={disabled}
          className={`ay-fv2-tile ay-fv2-tile--${tiles.selected_style ?? 'tint'}${current === '' ? ' ay-fv2-tile--on ay-fv2-tile--tone-neutral' : ''}`}
          data-testid={`tile-pending-${entry.field.field_id}`}
          onClick={() => onChange(null)}
        >
          {optionVisual(entry) === 'dot' && <span className="ay-fv2-dot ay-fv2-dot--neutral" aria-hidden="true" />}
          <span className="ay-fv2-tile__label">{pendingLabel}</span>
        </button>
      )}
      {options.map((option) => {
        const on = current === option.value;
        const danger = on && dangerOf?.(option.value) === true;
        return (
          <button
            key={option.value}
            id={`${id}-${option.value}`}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            className={`ay-fv2-tile ay-fv2-tile--${tiles.selected_style ?? 'tint'}${on ? ' ay-fv2-tile--on' : ''}${danger ? ' ay-fv2-tile--danger' : ''}${on && option.color !== undefined && optionVisual(entry) === 'dot' ? ` ay-fv2-tile--tone-${option.color}` : ''}`}
            onClick={() => {
              const next = boolean ? option.value === 'true' : option.value;
              onChange(on && !required ? null : next);
            }}
          >
            <OptionVisualMark entry={entry} option={option} size={visualSize} />
            <span className="ay-fv2-tile__label">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Choix segmenté (`segmented`) : quelques options côte à côte, la choisie en relief ou pleine. */
export function Segmented({ id, entry, value, onChange, disabled, required, labelledBy }: { id: string; entry: Entry; value: unknown; onChange: (value: unknown) => void; disabled: boolean; required: boolean; labelledBy?: string }) {
  const style = presentationOf(entry).segmented?.selected_style ?? 'raised';
  const current = value === null || value === undefined ? '' : String(value);
  const boolean = entry.field.type === 'boolean';
  return (
    <div className={`ay-fv2-segmented ay-fv2-segmented--${style}`} role="radiogroup" aria-labelledby={labelledBy} id={id}>
      {choiceOptions(entry).map((option) => {
        const on = current === option.value;
        return (
          <button key={option.value} type="button" role="radio" aria-checked={on} disabled={disabled} className={`ay-fv2-segmented__option${on ? ' ay-fv2-segmented__option--on' : ''}`} onClick={() => onChange(on && !required ? null : boolean ? option.value === 'true' : option.value)}>
            {option.icon !== undefined && <Icon name={option.icon} size={16} />}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
